'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useOptionalAccessControlState,
  useOptionalAdminStatus,
  useOptionalAppShell,
} from '@/components/shell/AppShellContext';
import type { Role } from '@/lib/access-control/types';
import { hasPermission as checkHasPermission } from '@/lib/access-control/permissions';
import logger from '@/lib/logger';
import { notify } from '@/lib/notifications';
import { dedupedFetchJson, invalidateDedupedFetch } from '@/lib/utils/dedupedFetch';

const MASQUERADE_URL = '/api/access-control/masquerade';

/** The signed-in user's real (never impersonated) highest-privilege role. */
export interface CurrentRole {
  /** null for a platform administrator with no explicit role row */
  id: string | null;
  name: string;
  slug: string;
  privilege_level: number;
  color: string | null;
  icon: string | null;
}

export interface ActivateMasqueradeOptions {
  /** Audit tag only; the server whitelists it and defaults to 'dialog'. */
  source?: 'login' | 'dialog';
}

export interface DeactivateMasqueradeOptions {
  /**
   * Reject when the server did not confirm the masquerade ended. The login picker needs this so it
   * never tells the user they are back on their own role while the masquerade is still in force.
   * Other callers (banner, header, testing console) are click handlers and keep the log-only behaviour.
   */
  throwOnError?: boolean;
}

export interface UseMasqueradeResult {
  /** Whether masquerade is currently active */
  isActive: boolean;
  /** Role IDs being masqueraded */
  roleIds: string[];
  /** Role names being masqueraded */
  roleNames: string[];
  /** Whether the user has permission to masquerade */
  canMasquerade: boolean;
  /** Roles available for masquerading (at or below user's privilege level) */
  availableRoles: Role[];
  /** The user's real highest-privilege role, once loaded (null until then or if none) */
  currentRole: CurrentRole | null;
  /** Role IDs in the masquerade cookie as reported by the server (informational) */
  activeRoleIds: string[];
  /** A masquerade cookie left from an earlier sign-in exists (it is ignored by the server, not in force) */
  staleMasquerade: boolean;
  /** Opaque value that changes with every sign-in; the login picker asks once per value (null until loaded) */
  promptKey: string | null;
  /** Activate masquerade with selected role IDs */
  activateMasquerade: (roleIds: string[], options?: ActivateMasqueradeOptions) => Promise<void>;
  /** End masquerade and restore original permissions */
  deactivateMasquerade: (options?: DeactivateMasqueradeOptions) => Promise<void>;
  /** Loading state */
  loading: boolean;
}

export function useMasquerade(): UseMasqueradeResult {
  const appShell = useOptionalAppShell();
  const accessControlState = useOptionalAccessControlState();
  const adminStatus = useOptionalAdminStatus();

  const [fallbackState, setFallbackState] = useState<{
    isActive: boolean;
    roleIds: string[];
    roleNames: string[];
  }>({
    isActive: false,
    roleIds: [],
    roleNames: [],
  });
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [currentRole, setCurrentRole] = useState<CurrentRole | null>(null);
  const [activeRoleIds, setActiveRoleIds] = useState<string[]>([]);
  const [promptKey, setPromptKey] = useState<string | null>(null);
  const [staleMasquerade, setStaleMasquerade] = useState(false);
  const [loading, setLoading] = useState(false);

  const usingSharedState = Boolean(accessControlState);
  const isActive = usingSharedState
    ? Boolean(accessControlState?.isMasquerading)
    : fallbackState.isActive;
  const roleIds = usingSharedState
    ? accessControlState?.masqueradeRoleIds || []
    : fallbackState.roleIds;
  const sharedRoleNames = usingSharedState
    ? accessControlState?.masqueradeRoleNames || []
    : fallbackState.roleNames;

  const canMasquerade =
    Boolean(adminStatus?.isPlatformAdmin) ||
    checkHasPermission(accessControlState?.userContext || null, 'roles.masquerade');

  useEffect(() => {
    if (!canMasquerade) {
      setAvailableRoles([]);
      setCurrentRole(null);
      setActiveRoleIds([]);
      setStaleMasquerade(false);
      return;
    }

    let cancelled = false;

    async function fetchRoles() {
      try {
        const data = await dedupedFetchJson<{
          roles?: any[];
          current_role?: CurrentRole | null;
          active_role_ids?: string[];
          prompt_key?: string;
          stale_masquerade?: boolean;
        }>(MASQUERADE_URL, {
          throwOnError: false,
        });
        if (cancelled) return;

        const roles: Role[] = (data?.roles || []).map((role: any) => ({
          id: role.id,
          tenant_org_unit_id: role.tenant_org_unit_id ?? null,
          name: role.name,
          slug: role.slug || '',
          description: role.description ?? null,
          privilege_level: role.privilege_level ?? 0,
          is_system_role: role.is_system_role ?? false,
          is_assignable: role.is_assignable ?? true,
          is_default: role.is_default ?? false,
          color: role.color ?? null,
          icon: role.icon ?? null,
          badge_url: role.badge_url ?? null,
          created_at: role.created_at || '',
          updated_at: role.updated_at || '',
        }));

        setAvailableRoles(roles);
        setCurrentRole(data?.current_role ?? null);
        setActiveRoleIds(Array.isArray(data?.active_role_ids) ? data.active_role_ids : []);
        setStaleMasquerade(data?.stale_masquerade === true);
        setPromptKey(typeof data?.prompt_key === 'string' && data.prompt_key ? data.prompt_key : null);
      } catch (error) {
        logger.warn('useMasquerade', 'Failed to fetch available roles', { error });
      }
    }

    fetchRoles();

    return () => {
      cancelled = true;
    };
  }, [canMasquerade]);

  const activateMasquerade = useCallback(async (
    selectedRoleIds: string[],
    options?: ActivateMasqueradeOptions,
  ) => {
    if (!canMasquerade || selectedRoleIds.length === 0) return;

    setLoading(true);
    try {
      const response = await fetch('/api/access-control/masquerade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role_ids: selectedRoleIds,
          source: options?.source ?? 'dialog',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error?.previous_ended === true) {
          // The server ended the old masquerade before this start failed. With no AppShell nothing
          // else refreshes this hook's own state, so stop showing it here (review r11 #5).
          setFallbackState({ isActive: false, roleIds: [], roleNames: [] });
          setActiveRoleIds([]);
        }
        throw new Error(error.error || 'Failed to activate masquerade');
      }

      const data = await response.json();

      if (appShell) {
        await appShell.refetchPermissions();
      } else {
        setFallbackState({
          isActive: true,
          roleIds: data.role_ids,
          roleNames: data.role_names,
        });
      }

      // Invalidate caches and signal NavigationProvider to refetch
      invalidateDedupedFetch(MASQUERADE_URL);
      localStorage.removeItem('neutralaiz-navigation-cache');
      window.dispatchEvent(new CustomEvent('masquerade-changed'));

      logger.info('useMasquerade', 'Masquerade activated', {
        roleIds: data.role_ids,
        roleNames: data.role_names,
      });
    } catch (error) {
      logger.error('useMasquerade', 'Failed to activate masquerade', { error });
      // A failed start can still have changed what the server holds: a switch ends the old
      // masquerade first and, if the new one cannot be recorded, clears its cookies. Show what the
      // server now says instead of the masquerade the page was showing (review r10).
      invalidateDedupedFetch(MASQUERADE_URL);
      try {
        localStorage.removeItem('neutralaiz-navigation-cache');
      } catch {
        // storage unavailable: nothing cached to drop
      }
      window.dispatchEvent(new CustomEvent('masquerade-changed'));
      try {
        await appShell?.refetchPermissions();
      } catch (refetchError) {
        logger.warn('useMasquerade', 'Could not refresh state after a failed start', { refetchError });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [appShell, canMasquerade]);

  const deactivateMasquerade = useCallback(async (options?: DeactivateMasqueradeOptions) => {
    setLoading(true);
    try {
      const response = await fetch('/api/access-control/masquerade', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to end the masquerade');
      }

      if (appShell) {
        await appShell.refetchPermissions();
      } else {
        setFallbackState({
          isActive: false,
          roleIds: [],
          roleNames: [],
        });
      }

      // Invalidate caches and signal NavigationProvider to refetch
      invalidateDedupedFetch(MASQUERADE_URL);
      localStorage.removeItem('neutralaiz-navigation-cache');
      window.dispatchEvent(new CustomEvent('masquerade-changed'));

      logger.info('useMasquerade', 'Masquerade deactivated');
    } catch (error) {
      logger.error('useMasquerade', 'Failed to deactivate masquerade', { error });
      if (options?.throwOnError === true) throw error;
      // A click handler (banner, header, testing console) has nowhere else to show it. The server
      // answers 503 "still in force, try again or sign out" when it cannot record the end, and the
      // banner correctly stays up, so say why (review r8 #5).
      notify.error(
        error instanceof Error && error.message ? error.message : 'Unable to end the masquerade. Try again, or sign out.',
      );
    } finally {
      setLoading(false);
    }
  }, [appShell]);

  const roleNames = useMemo(() => {
    if (sharedRoleNames.length > 0) {
      return sharedRoleNames;
    }

    if (roleIds.length === 0) {
      return [];
    }

    const roleNameById = new Map(availableRoles.map((role) => [role.id, role.name]));
    return roleIds
      .map((roleId) => roleNameById.get(roleId))
      .filter((name): name is string => Boolean(name));
  }, [availableRoles, roleIds, sharedRoleNames]);

  return useMemo(() => ({
    isActive,
    roleIds,
    roleNames,
    canMasquerade,
    availableRoles,
    currentRole,
    activeRoleIds,
    promptKey,
    staleMasquerade,
    activateMasquerade,
    deactivateMasquerade,
    loading,
  }), [
    isActive,
    roleIds,
    roleNames,
    canMasquerade,
    availableRoles,
    currentRole,
    activeRoleIds,
    promptKey,
    staleMasquerade,
    activateMasquerade,
    deactivateMasquerade,
    loading,
  ]);
}
